import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, RotateCcw, Redo2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusPill } from '@/components/ui/status-pill';
import { useUndo } from '@/contexts/UndoContext';
import { formatDateTime, formatTimeAgo } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { humanizeUndoLabel, isRedo, undoWhere } from '@/components/entry/undo-text';

const STATUS_TEXT = {
  undone: { label: 'Undone', tone: 'gray' },
  superseded: { label: 'Changed since', tone: 'amber' },
  canceled: { label: 'Cancelled', tone: 'outline' },
  partial: { label: 'Partly undone', tone: 'amber' },
};

/**
 * A change that is still `available` but that the caller's role may not undo (`restorable: false`, BUG-001): the
 * pill names why in two words, the full reason (the server's 403 detail) is its tooltip and accessible name.
 */
export const blockedUndoPill = (reason) => {
  const text = String(reason || '');
  if (/approval/i.test(text)) return { label: 'Needs approval', tone: 'gray' };
  if (/^(super )?admin only$/i.test(text.trim())) return { label: 'Admin only', tone: 'gray' };
  return { label: 'View only', tone: 'gray' };
};

/**
 * "Recent changes" — the signed-in user's last 10 changes with dates. Restorable ones can be undone
 * (always after a confirmation that lists exactly what will happen).
 */
export function RecentChangesDialog({ open, onOpenChange, isAdmin = false }) {
  const { fetchRecent, requestUndo } = useUndo();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      setItems(await fetchRecent(10));
    } catch (err) {
      setItems([]);
      setError(getErrorMessage(err, 'Could not load your recent changes'));
    }
  }, [fetchRecent]);

  useEffect(() => {
    if (open) {
      setItems(null);
      load();
    }
  }, [open, load]);

  const handleUndo = async (item) => {
    try {
      await requestUndo(item, { source: 'menu' });
    } catch (err) {
      setError(getErrorMessage(err, 'Could not undo the change'));
    } finally {
      // Reload whatever happened: an undo the server refused ("changed since") marks the entry
      // superseded, so its row must stop offering Undo even though the user then pressed Cancel.
      try {
        setItems(await fetchRecent(10));
      } catch {
        // keep the current list; the error (if any) is already shown
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 rounded-[18px] border-line bg-surface p-0 text-ink" data-testid="recent-changes-dialog">
        <DialogHeader className="border-b border-line-2 px-5 pb-3.5 pt-[18px] text-left">
          <DialogTitle className="flex items-center gap-2 font-heading text-[17px] font-bold text-ink">
            <History className="h-[18px] w-[18px] text-ink-3" aria-hidden="true" />
            Recent changes
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-ink-3">
            Your last 10 changes. Undo works while nobody has edited the same records since.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[min(60vh,520px)] overflow-y-auto">
          {items === null ? (
            <div className="space-y-2 p-5" role="status">
              <span className="sr-only">Loading</span>
              {[0, 1, 2].map((i) => <div key={i} className="ui-skeleton-shimmer h-11 rounded-lg" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={History}
              title={error ? 'Couldn’t load your changes' : 'No changes yet'}
              text={error || 'Changes you make to projects, stock, drawings and invoices show up here so you can undo them.'}
              tone={error ? 'red' : undefined}
              titleAs="h3"
              className="py-10"
            />
          ) : (
            <ul className="divide-y divide-[color:var(--line-2)]" data-testid="recent-changes-list">
              {items.map((item) => {
                const redo = isRedo(item);
                const where = undoWhere(item);
                const status = STATUS_TEXT[item.status]
                  || (item.status === 'available' && item.restorable === false ? blockedUndoPill(item.blocked_reason) : null);
                return (
                  <li key={item.id} className="flex items-center gap-3 px-5 py-3" data-testid="recent-change">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13.5px] font-semibold text-ink">{humanizeUndoLabel(item.label)}</p>
                      <p className="truncate text-[12.5px] text-ink-3">
                        {where ? `${where} · ` : ''}
                        <time dateTime={item.created_at} title={formatDateTime(item.created_at)}>
                          {formatTimeAgo(item.created_at)}
                        </time>
                      </p>
                    </div>
                    {item.restorable ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleUndo(item)}
                        data-testid="recent-change-undo"
                      >
                        {redo ? <Redo2 /> : <RotateCcw />}
                        {redo ? 'Redo' : 'Undo'}
                      </Button>
                    ) : status ? (
                      <span
                        title={item.blocked_reason || undefined}
                        aria-label={item.blocked_reason ? `${status.label}: ${item.blocked_reason}` : undefined}
                        data-testid="recent-change-status"
                      >
                        <StatusPill tone={status.tone} label={status.label} size="sm" />
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {error && items?.length ? (
            <p className="border-t border-line-2 px-5 py-2.5 text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</p>
          ) : null}
        </div>

        {isAdmin ? (
          <div className="flex justify-end border-t border-line-2 px-5 py-3">
            <Link
              to="/admin?tab=history"
              onClick={() => onOpenChange(false)}
              className="text-[13px] font-semibold text-brand-ink hover:underline"
            >
              Everyone’s changes: Admin › History →
            </Link>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
